import { useEffect, useMemo, useState } from "react";
import { Modal } from "antd";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../../authentication/authHelpers";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import { ModalXIcon } from "../../../assets/icons";
import TicketList from "./TicketList";
import TicketThread from "./TicketThread";
import ComposeTicketForm from "./ComposeTicketForm";
import { useTicketMessages } from "./hooks/useTicketMessages";
import {
  TICKET_STATUSES,
  isElevatedRole,
  stripHtmlToText,
  toMessagePreview,
} from "./utils/ticketStatus";
import "./supportInbox.scss";

function SupportInbox({ initialTicketId }) {
  const { userRole, employeeId } = useAuth();
  const { problematikat } = useSelector((state) => state.problematikat);
  const { isDarkMode } = useSelector((state) => state.usersList);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedId, setSelectedId] = useState(
    initialTicketId || searchParams.get("ticket") || ""
  );
  const [composeOpen, setComposeOpen] = useState(false);
  const isAdmin = isElevatedRole(userRole);

  const { sendReply, updateTicket, deleteTicket, sending } = useTicketMessages();

  useEffect(() => {
    const fromUrl = searchParams.get("ticket");
    if (fromUrl) setSelectedId(fromUrl);
  }, [searchParams]);

  // Employees only see their own tickets; admins/supervisors see all.
  const scopedTickets = useMemo(() => {
    const all = problematikat || [];
    if (isAdmin) return all;
    if (!employeeId) return [];
    return all.filter((t) => t.employeeId === employeeId);
  }, [problematikat, isAdmin, employeeId]);

  const statusCounts = useMemo(
    () =>
      TICKET_STATUSES.map((s) => ({
        ...s,
        count: scopedTickets.filter((t) => t.ticketStatus === s.value).length,
      })),
    [scopedTickets]
  );

  const filteredTickets = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...scopedTickets]
      .filter((t) => {
        if (statusFilter && t.ticketStatus !== statusFilter) return false;
        if (!q) return true;
        const hay = [
          t.createdBy,
          t.ticketCategory,
          t.lastMessagePreview,
          stripHtmlToText(t.ticketDescription),
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort(
        (a, b) =>
          (b.lastMessageAt || b.createdAt || 0) -
          (a.lastMessageAt || a.createdAt || 0)
      );
  }, [scopedTickets, search, statusFilter]);

  const selectedTicket =
    filteredTickets.find((t) => t.ticketId === selectedId) ||
    scopedTickets.find((t) => t.ticketId === selectedId) ||
    null;

  const selectTicket = (ticket) => {
    setSelectedId(ticket.ticketId);
    setSearchParams({ ticket: ticket.ticketId });
  };

  const handleCreated = (ticket) => {
    setComposeOpen(false);
    if (ticket?.ticketId) {
      setSelectedId(ticket.ticketId);
      setSearchParams({ ticket: ticket.ticketId });
    }
  };

  const handleStatusChange = async (ticket, value) => {
    await updateTicket(ticket, {
      ticketStatus: value,
      lastMessagePreview:
        ticket.lastMessagePreview ||
        toMessagePreview(ticket.ticketDescription),
    });
  };

  const handleComplete = async (ticket) => {
    await updateTicket(ticket, { ticketStatus: "Completed" });
  };

  const handleDelete = async (ticket) => {
    await deleteTicket(ticket);
    setSelectedId("");
    setSearchParams({});
  };

  return (
    <div
      className={`supportInbox ${isDarkMode ? "supportInbox--dark" : ""}`}
    >
      <div className="supportInbox__toolbar">
        <h1>Support</h1>
        <MondayButton
          className="mondayButtonBlue"
          hasIcon={false}
          onClick={() => setComposeOpen(true)}
        >
          {isAdmin ? "Krijoni ticket" : "Hap ticket"}
        </MondayButton>
      </div>

      <div className="supportInbox__layout">
        <TicketList
          tickets={filteredTickets}
          selectedId={selectedId}
          onSelect={selectTicket}
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          statusCounts={statusCounts}
        />
        <TicketThread
          ticket={selectedTicket}
          userRole={userRole}
          employeeId={employeeId}
          sending={sending}
          onSend={sendReply}
          onStatusChange={handleStatusChange}
          onComplete={handleComplete}
          onDelete={handleDelete}
        />
      </div>

      <Modal
        open={composeOpen}
        onCancel={() => setComposeOpen(false)}
        footer={null}
        width={720}
        title="Ticket i ri"
        closeIcon={<ModalXIcon />}
        destroyOnHidden
        className={isDarkMode ? "dark darkHeader" : "lightHeader"}
      >
        <ComposeTicketForm
          onCreated={handleCreated}
          onCancel={() => setComposeOpen(false)}
        />
      </Modal>
    </div>
  );
}

export default SupportInbox;
